import React, { useState, useMemo } from 'react'

interface Cliente {
  id: string
  nome_completo: string
  nome_empresa: string | null
  email: string | null
  celular: string | null
  cidade: string | null
  status: string
  data_cadastro: string
}

interface ClientesPorEstadoModalProps {
  isOpen: boolean
  onClose: () => void
  uf: string
  nomeEstado: string
  regiao: string
  clientes: Cliente[]
  totalClientes: number
  clientesAtivos: number
  clientesInativos: number
}

type FiltroStatus = 'todos' | 'ativo' | 'inativo'

const ClientesPorEstadoModal: React.FC<ClientesPorEstadoModalProps> = ({
  isOpen,
  onClose,
  uf,
  nomeEstado,
  regiao,
  clientes,
  totalClientes,
  clientesAtivos,
  clientesInativos
}) => {
  const [filtroStatus, setFiltroStatus] = useState<FiltroStatus>('todos')

  const clientesFiltrados = useMemo(() => {
    if (filtroStatus === 'todos') return clientes
    return clientes.filter(cliente => cliente.status === filtroStatus)
  }, [clientes, filtroStatus])

  if (!isOpen) return null

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Clientes de {nomeEstado}</h2>
                  <p className="text-white text-opacity-90 text-sm">
                    {regiao} • {totalClientes} clientes ({clientesAtivos} ativos, {clientesInativos} inativos)
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="px-6 pt-4 pb-2 border-b">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFiltroStatus('todos')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filtroStatus === 'todos'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Todos ({totalClientes})
              </button>
              <button
                onClick={() => setFiltroStatus('ativo')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filtroStatus === 'ativo'
                    ? 'bg-green-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Ativos ({clientesAtivos})
              </button>
              <button
                onClick={() => setFiltroStatus('inativo')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filtroStatus === 'inativo'
                    ? 'bg-red-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Inativos ({clientesInativos})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {clientesFiltrados.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-500">Nenhum cliente encontrado neste estado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clientesFiltrados.map((cliente) => (
                  <div 
                    key={cliente.id}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-3 h-3 rounded-full mt-1 ${
                          cliente.status === 'ativo' ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{cliente.nome_completo}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              cliente.status === 'ativo' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {cliente.status === 'ativo' ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                          {cliente.nome_empresa && (
                            <p className="text-sm text-gray-600 mb-2">
                              🏢 {cliente.nome_empresa}
                              {cliente.cidade && ` • ${cliente.cidade}`}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                            {cliente.email && (
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {cliente.email}
                              </span>
                            )}
                            {cliente.celular && (
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {cliente.celular}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            Cadastrado em {formatDate(cliente.data_cadastro)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Mostrando {clientesFiltrados.length} de {totalClientes} clientes
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ClientesPorEstadoModal
